import React from 'react';
import { Check, X } from 'lucide-react';
import type { PasswordStrength } from '../../utils/validations';

interface PasswordRequirementsProps {
  strength: PasswordStrength;
  className?: string;
}

const REQUIREMENT_LABELS: ReadonlyArray<{ key: keyof PasswordStrength; label: string }> = [
  { key: 'minLength', label: 'At least 8 characters' },
  { key: 'hasUppercase', label: 'One uppercase letter' },
  { key: 'hasLowercase', label: 'One lowercase letter' },
  { key: 'hasNumber', label: 'One number' },
  { key: 'hasSpecialChar', label: 'One special character' },
];

/**
 * Every password rule, always listed together, so the user learns all of them up front
 * instead of discovering the next one after satisfying the previous.
 */
const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ strength, className = '' }) => (
  <div className={`px-3 py-2 bg-gray-50 rounded-lg ${className}`}>
    <p className="text-xs font-medium text-charcoal mb-1">Password Requirements:</p>
    <ul className="flex flex-wrap gap-x-3 gap-y-0.5">
      {REQUIREMENT_LABELS.map(({ key, label }) => {
        const satisfied = strength[key];
        const StatusIcon = satisfied ? Check : X;
        return (
          <li
            key={key}
            data-satisfied={satisfied}
            className={`flex items-center text-[11px] leading-snug ${satisfied ? 'text-green-600' : 'text-gray-500'}`}
          >
            <StatusIcon
              className={`w-3 h-3 mr-1 shrink-0 ${satisfied ? 'text-green-600' : 'text-gray-400'}`}
              aria-hidden="true"
            />
            {label}
          </li>
        );
      })}
    </ul>
  </div>
);

export default PasswordRequirements;
